import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Eye, ClipboardList, GripVertical, ChevronDown, Sparkles, ShieldCheck, Share2, Upload } from "lucide-react";
import SubmissionHeatmap from "./SubmissionHeatmap";
import BulkFormImport from "./import/BulkFormImport";

const FIELD_TYPES = [
  { v: "text", lbl: "Short text" },
  { v: "textarea", lbl: "Paragraph" },
  { v: "number", lbl: "Number" },
  { v: "password", lbl: "Password" },
  { v: "select", lbl: "Dropdown" },
  { v: "radio", lbl: "Radio" },
  { v: "checkbox", lbl: "Checkboxes" },
  { v: "datetime", lbl: "Date/time" },
  { v: "location", lbl: "Location" },
  { v: "range", lbl: "Slider" },
  { v: "file", lbl: "File upload" },
  { v: "hidden", lbl: "Hidden" },
];
const KEY_RX = /^[a-z][a-z0-9_]*$/;

// ============================================================
// Field-key suggestions
// ============================================================
const STOPWORDS = new Set([
  "a", "an", "and", "or", "the", "of", "for", "to", "in", "on",
  "at", "is", "are", "was", "were", "be", "by", "with",
]);

const TYPE_FALLBACK = {
  text: ["text", "value", "answer"],
  textarea: ["notes", "description", "comments"],
  password: ["password", "pass", "secret"],
  select: ["choice", "option", "selection"],
  radio: ["choice", "selection"],
  checkbox: ["choices", "tags", "options"],
  datetime: ["timestamp", "date", "occurred_at", "submitted_at"],
  location: ["location", "place", "city", "site", "village"],
  range: ["score", "rating", "level"],
  file: ["file", "attachment", "upload"],
  hidden: ["hidden", "metadata"],
};

function snakeify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/'s\b/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "_$1");
}

function suggestKeys(label, type, existingKeys = [], popularKeys = [], canonicalKeys = []) {
  const taken = new Set((existingKeys || []).filter(Boolean));
  const out = [];
  const meta = {}; // key -> {popular?, canonical?}
  const push = (cand, extras) => {
    if (!cand) return;
    if (!KEY_RX.test(cand)) return;
    if (taken.has(cand)) return;
    if (out.includes(cand)) {
      if (extras) meta[cand] = { ...meta[cand], ...extras };
      return;
    }
    out.push(cand);
    if (extras) meta[cand] = { ...extras };
  };
  const lower = (label || "").trim().toLowerCase();
  const allTokens = lower.split(/[^a-z0-9]+/).filter(Boolean);
  const meaningful = allTokens.filter((t) => !STOPWORDS.has(t));
  const tokenSet = new Set(meaningful);

  // 0a) Canonical (admin-promoted) keys whose label/key share a meaningful
  //     token with this label. These are workspace standards — show them
  //     first with the strongest visual treatment.
  if (tokenSet.size > 0 && Array.isArray(canonicalKeys) && canonicalKeys.length) {
    for (const ck of canonicalKeys) {
      if (!ck?.key || !KEY_RX.test(ck.key)) continue;
      const labelTokens = new Set();
      for (const tk of (ck.label || "").toLowerCase().split(/[^a-z0-9]+/)) {
        if (tk && !STOPWORDS.has(tk)) labelTokens.add(tk);
      }
      for (const tk of ck.key.split("_")) labelTokens.add(tk);
      let overlap = 0;
      for (const tk of tokenSet) if (labelTokens.has(tk)) overlap += 1;
      if (overlap >= 1) {
        push(ck.key, { canonical: { label: ck.label, description: ck.description } });
      }
    }
  }

  // 0b) Popular keys whose own labels share at least one meaningful token
  //     with this label. (Skipped if already added as canonical.)
  if (tokenSet.size > 0 && Array.isArray(popularKeys) && popularKeys.length) {
    const scored = [];
    for (const pk of popularKeys) {
      if (!pk?.key || !KEY_RX.test(pk.key)) continue;
      const labelTokens = new Set();
      for (const lbl of pk.labels || []) {
        for (const tk of (lbl || "").toLowerCase().split(/[^a-z0-9]+/)) {
          if (tk && !STOPWORDS.has(tk)) labelTokens.add(tk);
        }
      }
      for (const tk of pk.key.split("_")) labelTokens.add(tk);
      let overlap = 0;
      for (const tk of tokenSet) if (labelTokens.has(tk)) overlap += 1;
      if (overlap >= 1) {
        scored.push({ pk, overlap });
      }
    }
    scored.sort((a, b) => (b.overlap - a.overlap) || ((b.pk.count || 0) - (a.pk.count || 0)));
    for (const { pk } of scored.slice(0, 3)) {
      push(pk.key, { popular: { count: pk.count, labels: pk.labels || [] } });
    }
  }

  // 1) Full snake_case, keeping stopwords ("date of birth" -> "date_of_birth")
  if (allTokens.length) push(snakeify(allTokens.join(" ")));
  // 2) Snake_case with only meaningful tokens (distinct from #1 only when stopwords are present)
  if (meaningful.length) push(snakeify(meaningful.join(" ")));
  // 3) Last meaningful token (good for shorthand: "Number of children enrolled" -> "enrolled")
  if (meaningful.length) push(snakeify(meaningful[meaningful.length - 1]));
  // 4) First two meaningful tokens ("Number of children enrolled" -> "number_children")
  if (meaningful.length >= 2) push(snakeify(meaningful.slice(0, 2).join("_")));
  // 5) Last two meaningful tokens ("Number of children enrolled" -> "children_enrolled")
  if (meaningful.length >= 3) push(snakeify(meaningful.slice(-2).join("_")));
  // 6) First meaningful token alone (only adds value when there are 2+ meaningful tokens)
  if (meaningful.length >= 2) push(snakeify(meaningful[0]));

  // 7) Type-based fallbacks only when the label gave us nothing
  if (out.length === 0) {
    for (const t of TYPE_FALLBACK[type] || []) {
      push(snakeify(t));
    }
  }

  return out.slice(0, 6).map((k) => ({
    key: k,
    popular: meta[k]?.popular || null,
    canonical: meta[k]?.canonical || null,
  }));
}

function blankField(type = "text") {
  const base = { id: `tmp-${Math.random().toString(36).slice(2, 8)}`, type, key: "", label: "", required: false };
  if (["select", "radio", "checkbox"].includes(type)) base.options = ["Option 1", "Option 2"];
  if (type === "range") Object.assign(base, { min: 0, max: 100, step: 1 });
  return base;
}

export default function FormsTab({ pid, indicators }) {
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // form being edited
  const [filling, setFilling] = useState(null); // form being submitted by viewer
  const [viewing, setViewing] = useState(null); // {form, submission} for the detail dialog
  const [sharing, setSharing] = useState(null); // form being shared publicly
  const [bulkImport, setBulkImport] = useState(false);

  const openSubmission = async (form, sid) => {
    try {
      const { data } = await api.get(`/forms/${form.id}/submissions/${sid}`);
      setViewing({ form, submission: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load submission");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/forms`);
      setForms(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid]);

  const loadSubs = async (fid) => {
    try {
      const { data } = await api.get(`/forms/${fid}/submissions`);
      setSubmissions((s) => ({ ...s, [fid]: data || [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load submissions");
    }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete form "${form.name}" and all its submissions?`)) return;
    try {
      await api.delete(`/forms/${form.id}`);
      toast.success("Form deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="flex items-center justify-between">
        <p className="label-overline">Field-worker forms</p>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setBulkImport(true)}
            variant="outline"
            data-testid="bulk-import-form-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> Bulk import
          </Button>
          <Button
            onClick={() => setActive({ name: "", description: "", status: "draft", fields: [], indicator_id: null })}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="new-form-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New form
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : forms.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="forms-empty">
          <ClipboardList className="w-10 h-10 mx-auto text-slate-400" />
          <p className="mt-3 font-medium">No forms yet.</p>
          <p className="text-xs mt-1">Build a form so field workers can submit data straight from their phone.</p>
        </div>
      ) : (
        <ul className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
          {forms.map((f) => (
            <li key={f.id} className="p-4" data-testid={`form-row-${f.id}`}>
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-slate-400 shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{f.name}</span>
                    <Badge
                      variant="outline"
                      className={
                        f.status === "published"
                          ? "border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px] uppercase tracking-wider"
                          : f.status === "archived"
                          ? "border-slate-300 text-slate-500 bg-slate-50 text-[10px] uppercase tracking-wider"
                          : "border-amber-300 text-amber-700 bg-amber-50 text-[10px] uppercase tracking-wider"
                      }
                    >
                      {f.status}
                    </Badge>
                    {f.indicator_id && (
                      <span className="text-[11px] text-slate-500">→ feeds indicator</span>
                    )}
                  </div>
                  {f.description && <p className="text-xs text-slate-500 mt-1">{f.description}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">{(f.fields || []).length} fields · created {new Date(f.created_at).toLocaleDateString()}</p>
                  {submissions[f.id] && (
                    <div className="mt-3 space-y-3">
                      <SubmissionHeatmap submissions={submissions[f.id]} />
                      <div className="border border-slate-100 rounded-sm bg-slate-50 p-2">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recent submissions · {submissions[f.id].length}</p>
                        {submissions[f.id].length === 0 ? (
                          <p className="text-xs text-slate-500">No submissions yet.</p>
                        ) : (
                          <ul className="text-xs space-y-1">
                            {submissions[f.id].slice(0, 8).map((s) => (
                              <li key={s.id}>
                                <button
                                  type="button"
                                  onClick={() => openSubmission(f, s.id)}
                                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm hover:bg-white border border-transparent hover:border-slate-200 transition-colors text-left"
                                  data-testid={`submission-row-${s.id}`}
                                >
                                  <span className="text-slate-700 truncate flex items-center gap-1.5">
                                    <Eye className="w-3 h-3 text-slate-400 shrink-0" />
                                    {s.submitted_by_name || s.submitted_by_email}
                                  </span>
                                  <span className="font-mono text-slate-400 text-[10px] shrink-0">{new Date(s.submitted_at).toLocaleString()}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {f.status === "published" && (
                    <Button variant="outline" size="sm" onClick={() => setFilling(f)} data-testid={`fill-form-${f.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Fill
                    </Button>
                  )}
                  {f.status === "published" && (
                    <Button variant="outline" size="sm" onClick={() => setSharing(f)} data-testid={`share-form-${f.id}`}>
                      <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => loadSubs(f.id)} data-testid={`subs-form-${f.id}`}>
                    Submissions
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setActive(f)} data-testid={`edit-form-${f.id}`}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(f)} data-testid={`delete-form-${f.id}`}>
                    <Trash2 className="w-4 h-4 text-slate-500" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {active && (
        <FormBuilder
          pid={pid}
          form={active}
          indicators={indicators}
          onClose={() => setActive(null)}
          onSaved={() => { setActive(null); load(); }}
        />
      )}
      {filling && (
        <FormFiller
          form={filling}
          onClose={() => setFilling(null)}
          onSubmitted={() => { setFilling(null); load(); }}
        />
      )}
      {viewing && (
        <SubmissionViewer
          form={viewing.form}
          submission={viewing.submission}
          onClose={() => setViewing(null)}
        />
      )}
      {sharing && (
        <ShareFormDialog
          form={sharing}
          onClose={() => setSharing(null)}
        />
      )}
      {bulkImport && (
        <BulkFormImport
          pid={pid}
          indicators={indicators}
          open
          onClose={() => setBulkImport(false)}
          onImported={() => { setBulkImport(false); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Submission detail viewer
// ============================================================
async function exportSubmission(formId, submissionId, format) {
  try {
    const res = await api.get(
      `/forms/${formId}/submissions/${submissionId}/export?format=${format}`,
      { responseType: "blob" }
    );
    const blob = new Blob([res.data], {
      type: format === "pdf" ? "application/pdf" : "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submission-${submissionId.slice(0, 8)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error("Couldn't generate the export");
  }
}

function SubmissionViewer({ form, submission, onClose }) {
  const fieldsByKey = Object.fromEntries((form.fields || []).map((f) => [f.key, f]));
  const fieldOrder = (form.fields || []).map((f) => f.key);
  const answers = submission.answers || {};
  // Render in field-definition order; surface unknown keys at the end.
  const knownKeys = fieldOrder.filter((k) => k in answers);
  const extraKeys = Object.keys(answers).filter((k) => !fieldsByKey[k]);
  const ordered = [...knownKeys, ...extraKeys];

  const renderValue = (key, value) => {
    const f = fieldsByKey[key];
    if (value === null || value === undefined || value === "") {
      return <span className="text-slate-400 italic">— not provided —</span>;
    }
    if (f?.type === "file" && typeof value === "object" && value?.content_base64) {
      const href = `data:${value.mime_type || "application/octet-stream"};base64,${value.content_base64}`;
      const isImg = (value.mime_type || "").startsWith("image/");
      return (
        <div className="space-y-2">
          {isImg && (
            <img
              src={href}
              alt={value.filename}
              className="max-h-48 w-auto border border-slate-200 rounded-sm bg-white"
            />
          )}
          <a
            href={href}
            download={value.filename || "file"}
            className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"
            data-testid={`submission-file-${key}`}
          >
            <FileText className="w-3.5 h-3.5" /> {value.filename || "Download"}
            {value.size ? <span className="text-slate-400 ml-1">({Math.ceil(value.size / 1024)} KB)</span> : null}
          </a>
        </div>
      );
    }
    if (Array.isArray(value)) {
      return value.length ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm border border-slate-200">{String(v)}</span>
          ))}
        </div>
      ) : <span className="text-slate-400 italic">— empty —</span>;
    }
    if (typeof value === "object") {
      return <pre className="text-xs bg-slate-50 border border-slate-100 p-2 rounded-sm overflow-auto whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>;
    }
    if (f?.type === "textarea") {
      return <p className="text-sm text-slate-800 whitespace-pre-wrap">{String(value)}</p>;
    }
    if (f?.type === "datetime") {
      const d = new Date(value);
      return <span className="text-sm text-slate-800">{isNaN(d) ? String(value) : d.toLocaleString()}</span>;
    }
    return <span className="text-sm text-slate-800 break-words">{String(value)}</span>;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="submission-detail-dialog">
        <DialogHeader>
          <DialogTitle>Submission · {form.name}</DialogTitle>
          <DialogDescription>Detailed view of every answer captured in this submission.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 border border-slate-100 rounded-sm p-3">
          <div>
            <p className="label-overline">Submitted by</p>
            <p className="text-sm text-slate-800 mt-1" data-testid="submission-detail-by">
              {submission.submitted_by_name || submission.submitted_by_email || "—"}
            </p>
            {submission.submitted_by_email && submission.submitted_by_name && (
              <p className="text-[11px] text-slate-500">{submission.submitted_by_email}</p>
            )}
          </div>
          <div>
            <p className="label-overline">Submitted at</p>
            <p className="text-sm text-slate-800 mt-1 font-mono">
              {new Date(submission.submitted_at).toLocaleString()}
            </p>
          </div>
          {submission.data_row_id && (
            <div className="col-span-2">
              <p className="label-overline">Linked data row</p>
              <p className="text-[11px] font-mono text-slate-500 break-all">{submission.data_row_id}</p>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-4" data-testid="submission-detail-answers">
          {ordered.length === 0 && (
            <p className="text-sm text-slate-500">This submission has no answers.</p>
          )}
          {ordered.map((key) => {
            const f = fieldsByKey[key];
            const label = f?.label || key;
            return (
              <div key={key} className="border-b border-slate-100 last:border-b-0 pb-3" data-testid={`submission-answer-${key}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-slate-700">{label}</p>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{f?.type || "unknown"}</span>
                </div>
                <div className="mt-1.5">{renderValue(key, answers[key])}</div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex sm:flex-row sm:justify-between gap-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => exportSubmission(form.id, submission.id, "csv")}
              data-testid="submission-export-csv"
            >
              <FileText className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => exportSubmission(form.id, submission.id, "pdf")}
              data-testid="submission-export-pdf"
            >
              <FileText className="w-4 h-4 mr-1.5" /> Export PDF
            </Button>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="submission-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Form builder
// ============================================================
function FormBuilder({ pid, form, indicators, onClose, onSaved }) {
  const isEdit = Boolean(form.id);
  const [draft, setDraft] = useState({
    ...form,
    fields: (form.fields || []).map((f) => ({ ...f, options: f.options ? [...f.options] : f.options })),
    indicator_id: form.indicator_id || null,
    value_field_key: form.value_field_key || null,
    period_field_key: form.period_field_key || null,
  });
  const [saving, setSaving] = useState(false);
  const [popularKeys, setPopularKeys] = useState([]);
  const [canonicalKeys, setCanonicalKeys] = useState([]);
  // Auto-mapping toast key — prevents re-toasting on every keystroke if the
  // user keeps editing the same matching label.
  const [autoMappedField, setAutoMappedField] = useState(null);

  // Smart mapping: when the user adds/edits a field whose label matches one
  // of this program's indicator names (case + whitespace insensitive) and
  // the form is NOT yet wired to any indicator, auto-link them. Picks the
  // best numeric-eligible field as the value source so submissions feed
  // the report immediately.
  useEffect(() => {
    if (!Array.isArray(indicators) || indicators.length === 0) return;
    if (draft.indicator_id) return; // user has already chosen — respect it
    const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const byNorm = new Map();
    for (const i of indicators) {
      const norm = normalize(i.name);
      if (norm) byNorm.set(norm, i);
    }
    let matchedField = null;
    let matchedInd = null;
    for (const f of draft.fields || []) {
      // Label is the only signal we need — the slug-derived key may not yet
      // be committed to state at the moment the user finishes typing the
      // label, so requiring `f.key` here causes auto-mapping to silently
      // never fire. Match on label, then pick the field's key (or fall back
      // to the slug we'd auto-generate) when staging the value source.
      if (!f.label) continue;
      const ind = byNorm.get(normalize(f.label));
      if (ind) {
        matchedField = f;
        matchedInd = ind;
        break;
      }
    }
    if (matchedField && matchedInd) {
      // Only auto-pick value/period if the matched field can serve as a value
      // source (numeric-friendly) — otherwise just stage the indicator link.
      const isValueEligible = ["text", "range"].includes(matchedField.type);
      // Fall back to slug-of-label if the key hasn't propagated to state yet.
      const slugKey = (matchedField.label || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const valueKey = isValueEligible ? (matchedField.key || slugKey) : null;
      setDraft((d) => ({
        ...d,
        indicator_id: matchedInd.id,
        value_field_key: valueKey || d.value_field_key || null,
      }));
      if (autoMappedField !== (matchedField.label || "")) {
        toast.success(`Auto-linked to indicator "${matchedInd.code} — ${matchedInd.name}"`);
        // Use the label (not the possibly-empty key) as the de-dupe token so
        // (a) the badge — which checks autoMappedField truthiness — is always
        // visible after a successful match, and (b) we don't re-toast on
        // every render when the user keeps editing the same matching label.
        setAutoMappedField(matchedField.label);
      }
    }
  }, [draft.fields, indicators, draft.indicator_id, autoMappedField]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pop, dict] = await Promise.all([
          api.get("/forms/popular-keys"),
          api.get("/key-dictionary"),
        ]);
        if (!cancelled) {
          setPopularKeys(Array.isArray(pop.data) ? pop.data : []);
          setCanonicalKeys(Array.isArray(dict.data) ? dict.data : []);
        }
      } catch {
        // Silent — popular/canonical keys are nice-to-have; the local heuristics still work.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const addField = (type = "text") => {
    setDraft((d) => ({ ...d, fields: [...d.fields, blankField(type)] }));
  };
  const updateField = (idx, patch) => {
    setDraft((d) => {
      const fields = [...d.fields];
      fields[idx] = { ...fields[idx], ...patch };
      return { ...d, fields };
    });
  };
  const removeField = (idx) => {
    setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
  };
  const moveField = (idx, dir) => {
    setDraft((d) => {
      const fields = [...d.fields];
      const j = idx + dir;
      if (j < 0 || j >= fields.length) return d;
      [fields[idx], fields[j]] = [fields[j], fields[idx]];
      return { ...d, fields };
    });
  };

  const validateBeforeSave = () => {
    if (!draft.name.trim()) return "Form name is required.";
    const seen = new Set();
    for (const f of draft.fields) {
      // Normalize options at save-time so trailing blank lines while typing don't trip validation
      if (["select", "radio", "checkbox"].includes(f.type)) {
        f.options = (f.options || []).map((o) => (o || "").trim()).filter(Boolean);
      }
      if (!KEY_RX.test(f.key || "")) return `Field key "${f.key || "(empty)"}" is invalid. Use lowercase letters, digits, underscores; must start with a letter.`;
      if (seen.has(f.key)) return `Duplicate key "${f.key}". Field keys must be unique.`;
      seen.add(f.key);
      if (!f.label?.trim()) return `Field "${f.key}" needs a label.`;
      if (["select", "radio", "checkbox"].includes(f.type) && (!f.options || f.options.length === 0))
        return `Field "${f.key}" needs at least one option.`;
      if (f.type === "range" && (f.min == null || f.max == null || Number(f.max) <= Number(f.min)))
        return `Range field "${f.key}" needs min < max.`;
    }
    if (draft.indicator_id && !draft.value_field_key)
      return "When linking an indicator, pick which field becomes the indicator value.";
    return null;
  };

  const save = async (newStatus) => {
    const err = validateBeforeSave();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        description: draft.description?.trim() || "",
        status: newStatus || draft.status,
        fields: draft.fields,
        indicator_id: draft.indicator_id || null,
        value_field_key: draft.value_field_key || null,
        period_field_key: draft.period_field_key || null,
      };
      if (isEdit) {
        await api.patch(`/forms/${form.id}`, { ...body, clear_indicator: !draft.indicator_id });
      } else {
        await api.post(`/programs/${pid}/forms`, body);
      }
      toast.success(`Form ${newStatus === "published" ? "published" : "saved"}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save form");
    } finally {
      setSaving(false);
    }
  };

  const numericFields = draft.fields.filter((f) => ["text", "range"].includes(f.type) && (f.key || "").trim());
  const textFields = draft.fields.filter((f) => ["text", "datetime"].includes(f.type) && (f.key || "").trim());

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="form-builder-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit form" : "New form"}</DialogTitle>
          <DialogDescription>Add fields, link to an indicator if you want submissions to feed your report, then publish.</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 space-y-5">
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Form name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1" data-testid="form-name-input" />
            </div>
            <div>
              <Label className="label-overline">Description</Label>
              <Textarea value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-1" rows={2} data-testid="form-desc-input" />
            </div>
          </div>

          {/* Indicator wiring */}
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="form-indicator-wiring">
            <p className="label-overline mb-2 flex items-center gap-1.5">
              Wire to an indicator <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
              {draft.indicator_id && autoMappedField && (
                <span
                  className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm normal-case font-medium"
                  data-testid="form-auto-mapped-badge"
                  title="Array Engine matched a field label to an indicator name and linked them for you. Edit to override."
                >
                  <Sparkles className="w-2.5 h-2.5" /> Auto-linked
                </span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select value={draft.indicator_id || "__none__"} onValueChange={(v) => setDraft({ ...draft, indicator_id: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="form-indicator-select"><SelectValue placeholder="Indicator" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(indicators || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={draft.value_field_key || "__none__"} onValueChange={(v) => setDraft({ ...draft, value_field_key: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="form-value-key-select"><SelectValue placeholder="Value field" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Pick a field —</SelectItem>
                  {numericFields.map((f) => <SelectItem key={f.key || f.id} value={f.key}>{f.label || f.key}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={draft.period_field_key || "__none__"} onValueChange={(v) => setDraft({ ...draft, period_field_key: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="form-period-key-select"><SelectValue placeholder="Period field" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Pick a field —</SelectItem>
                  {textFields.map((f) => <SelectItem key={f.key || f.id} value={f.key}>{f.label || f.key}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">When set, every submission auto-creates a data row that feeds the report.</p>
          </div>

          {/* Fields */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="label-overline">Fields · {draft.fields.length}</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="add-field-select">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add field
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {FIELD_TYPES.map((t) => (
                    <DropdownMenuItem
                      key={t.v}
                      onSelect={() => addField(t.v)}
                      data-testid={`add-field-${t.v}`}
                    >
                      {t.lbl}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {draft.fields.length === 0 ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-200 p-6 text-center">Add at least one field to start.</p>
            ) : (
              <ul className="space-y-2" data-testid="form-fields-list">
                {draft.fields.map((f, idx) => (
                  <li key={f.id} className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`field-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-slate-300" />
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{FIELD_TYPES.find((t) => t.v === f.type)?.lbl || f.type}</Badge>
                      <div className="flex-1" />
                      <Button variant="ghost" size="icon" onClick={() => moveField(idx, -1)} disabled={idx === 0}>↑</Button>
                      <Button variant="ghost" size="icon" onClick={() => moveField(idx, 1)} disabled={idx === draft.fields.length - 1}>↓</Button>
                      <Button variant="ghost" size="icon" onClick={() => removeField(idx)} data-testid={`field-remove-${idx}`}><Trash2 className="w-4 h-4 text-slate-500" /></Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label className="label-overline">Label</Label>
                        <Input value={f.label} onChange={(e) => updateField(idx, { label: e.target.value })} className="mt-1" data-testid={`field-label-${idx}`} />
                      </div>
                      <div>
                        <Label className="label-overline">Key (lowercase)</Label>
                        <FieldKeyPicker
                          field={f}
                          allFields={draft.fields}
                          idx={idx}
                          popularKeys={popularKeys}
                          canonicalKeys={canonicalKeys}
                          onChange={(val) => updateField(idx, { key: val })}
                        />
                      </div>
                    </div>
                    {(f.type === "text" || f.type === "textarea" || f.type === "password" || f.type === "datetime" || f.type === "file" || f.type === "location") && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                        <Input placeholder="Placeholder" value={f.placeholder || ""} onChange={(e) => updateField(idx, { placeholder: e.target.value })} className="text-xs" />
                        <Input placeholder="Help text" value={f.help_text || ""} onChange={(e) => updateField(idx, { help_text: e.target.value })} className="text-xs" />
                      </div>
                    )}
                    {(f.type === "select" || f.type === "radio" || f.type === "checkbox") && (
                      <div className="mt-2">
                        <Label className="label-overline">Options (one per line)</Label>
                        <Textarea
                          value={(f.options || []).join("\n")}
                          onChange={(e) => updateField(idx, { options: e.target.value.split("\n") })}
                          onBlur={(e) => updateField(idx, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                          rows={Math.max(3, (f.options || []).length + 1)}
                          className="mt-1 font-mono text-xs"
                          data-testid={`field-options-${idx}`}
                          placeholder={`Option 1\nOption 2\nOption 3\n…`}
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Add as many options as you need — one per line.
                        </p>
                      </div>
                    )}
                    {f.type === "range" && (
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <Input type="number" placeholder="min" value={f.min ?? ""} onChange={(e) => updateField(idx, { min: e.target.value === "" ? null : Number(e.target.value) })} />
                        <Input type="number" placeholder="max" value={f.max ?? ""} onChange={(e) => updateField(idx, { max: e.target.value === "" ? null : Number(e.target.value) })} />
                        <Input type="number" placeholder="step" value={f.step ?? ""} onChange={(e) => updateField(idx, { step: e.target.value === "" ? null : Number(e.target.value) })} />
                      </div>
                    )}
                    {f.type === "hidden" && (
                      <div className="mt-2">
                        <Label className="label-overline">Default value</Label>
                        <Input value={f.default ?? ""} onChange={(e) => updateField(idx, { default: e.target.value })} className="mt-1 font-mono text-xs" />
                      </div>
                    )}
                    {f.type !== "hidden" && (
                      <label className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                        <input type="checkbox" checked={Boolean(f.required)} onChange={(e) => updateField(idx, { required: e.target.checked })} />
                        Required
                      </label>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="form-save-draft">Save draft</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => save("published")} disabled={saving} data-testid="form-save-publish">
            {saving ? "Saving…" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Field-key picker — input + suggestion dropdown
// ============================================================
function FieldKeyPicker({ field, allFields, idx, onChange, popularKeys = [], canonicalKeys = [] }) {
  const [open, setOpen] = useState(false);
  const otherKeys = (allFields || [])
    .map((f, i) => (i !== idx ? f.key : null))
    .filter(Boolean);
  const suggestions = suggestKeys(field.label, field.type, otherKeys, popularKeys, canonicalKeys);
  const suggestionKeys = suggestions.map((s) => s.key);
  const isCustom = !!field.key && !suggestionKeys.includes(field.key);
  const isInvalid = field.key && !KEY_RX.test(field.key);

  return (
    <div className="mt-1" data-testid={`field-key-wrapper-${idx}`}>
      <div className="flex gap-1">
        <Input
          value={field.key}
          onChange={(e) =>
            onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))
          }
          className={`font-mono flex-1 ${isInvalid ? "border-red-300 focus-visible:ring-red-300" : ""}`}
          data-testid={`field-key-${idx}`}
          placeholder={suggestionKeys[0] || "field_key"}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              data-testid={`field-key-suggest-${idx}`}
              title="Suggested keys based on the label"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 p-2"
            data-testid={`field-key-suggestions-${idx}`}
          >
            <div className="flex items-center gap-2 px-2 py-1 text-[11px] uppercase tracking-wider text-slate-500">
              <Sparkles className="w-3 h-3" />
              Suggested keys
            </div>
            <ul className="space-y-0.5 mt-1 max-h-64 overflow-y-auto">
              {suggestions.length === 0 && (
                <li className="px-2 py-3 text-xs text-slate-500">
                  Add a label to see suggestions.
                </li>
              )}
              {suggestions.map((s) => (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(s.key);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-2 py-1.5 rounded-sm flex items-center gap-2 hover:bg-slate-100 ${
                      field.key === s.key ? "bg-slate-900 text-white hover:bg-slate-900" : ""
                    }`}
                    data-testid={`field-key-option-${idx}-${s.key}`}
                  >
                    <span className="font-mono text-xs flex-1 truncate">{s.key}</span>
                    {s.canonical && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${
                          field.key === s.key
                            ? "bg-white/20 text-white"
                            : "bg-slate-900 text-white"
                        }`}
                        title={s.canonical.description || `Canonical: ${s.canonical.label}`}
                        data-testid={`field-key-canonical-${idx}-${s.key}`}
                      >
                        <ShieldCheck className="w-2.5 h-2.5" />
                        Canonical
                      </span>
                    )}
                    {s.popular && !s.canonical && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${
                          field.key === s.key
                            ? "bg-white/20 text-white"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        }`}
                        title={
                          (s.popular.labels || []).length
                            ? `Used as: ${(s.popular.labels || []).slice(0, 4).join(" · ")}`
                            : undefined
                        }
                        data-testid={`field-key-popular-${idx}-${s.key}`}
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        Used in {s.popular.count} form{s.popular.count === 1 ? "" : "s"}
                      </span>
                    )}
                  </button>
                </li>
              ))}
              {isCustom && KEY_RX.test(field.key) && (
                <>
                  <li className="border-t border-slate-100 mt-1 pt-1">
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">
                      Your custom key
                    </p>
                  </li>
                  <li>
                    <span
                      className="w-full block px-2 py-1.5 rounded-sm font-mono text-xs bg-slate-900 text-white"
                      data-testid={`field-key-custom-${idx}`}
                    >
                      {field.key}
                    </span>
                  </li>
                </>
              )}
            </ul>
            <p className="mt-2 px-2 text-[10px] text-slate-500 leading-relaxed">
              <ShieldCheck className="w-2.5 h-2.5 inline mr-0.5" />
              canonical keys are workspace standards · <Sparkles className="w-2.5 h-2.5 inline mx-0.5 text-emerald-600" />
              keys are already used in other published forms — picking either keeps your data joinable.
            </p>
          </PopoverContent>
        </Popover>
      </div>
      {isInvalid && (
        <p className="text-[11px] text-red-600 mt-1">
          Use lowercase letters, digits, underscores; must start with a letter.
        </p>
      )}
    </div>
  );
}

// ============================================================
// Form filler
// ============================================================
function FormFiller({ form, onClose, onSubmitted }) {
  const [answers, setAnswers] = useState(() => {
    const a = {};
    for (const f of form.fields || []) {
      if (f.type === "checkbox") a[f.key] = [];
      else if (f.type === "hidden") a[f.key] = f.default ?? "";
    }
    return a;
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));

  const onFile = (k, file) => {
    if (!file) { set(k, null); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result?.toString() || "";
      const base64 = dataUrl.split(",")[1] || "";
      set(k, { filename: file.name, mime_type: file.type || "application/octet-stream", content_base64: base64 });
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/forms/${form.id}/submissions`, { answers });
      toast.success("Submitted — thank you!");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="form-filler-dialog">
        <DialogHeader>
          <DialogTitle>{form.name}</DialogTitle>
          {form.description && <DialogDescription>{form.description}</DialogDescription>}
        </DialogHeader>
        <div className="overflow-y-auto pr-1 space-y-4">
          {(form.fields || []).filter((f) => f.type !== "hidden").map((f) => (
            <div key={f.id || f.key} data-testid={`fill-${f.key}`}>
              <Label className="label-overline">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              {f.help_text && <p className="text-[11px] text-slate-500 mt-0.5">{f.help_text}</p>}
              {f.type === "text" && (
                <Input className="mt-1" placeholder={f.placeholder || ""} value={answers[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
              {f.type === "textarea" && (
                <Textarea className="mt-1" rows={3} placeholder={f.placeholder || ""} value={answers[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
              {f.type === "password" && (
                <Input type="password" className="mt-1" placeholder={f.placeholder || ""} value={answers[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
              {f.type === "select" && (
                <Select value={answers[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "radio" && (
                <div className="mt-1 space-y-1">
                  {(f.options || []).map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm">
                      <input type="radio" name={f.key} value={o} checked={answers[f.key] === o} onChange={() => set(f.key, o)} />
                      {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === "checkbox" && (
                <div className="mt-1 space-y-1">
                  {(f.options || []).map((o) => {
                    const arr = answers[f.key] || [];
                    return (
                      <label key={o} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={arr.includes(o)}
                          onChange={(e) => set(f.key, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))}
                        />
                        {o}
                      </label>
                    );
                  })}
                </div>
              )}
              {f.type === "datetime" && (
                <Input type="datetime-local" className="mt-1" value={answers[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
              {f.type === "location" && (
                <div className="mt-1 space-y-1">
                  <Input
                    type="text"
                    placeholder={f.placeholder || "City, Country (or coordinates)"}
                    value={answers[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`location-input-${f.key}`}
                  />
                  <p className="text-[10px] text-slate-500">
                    Auto-stamps as the <span className="font-mono">Location</span> dimension on the dashboard.
                  </p>
                </div>
              )}
              {f.type === "range" && (
                <div className="mt-1">
                  <input type="range" className="w-full" min={f.min} max={f.max} step={f.step || 1} value={answers[f.key] ?? f.min} onChange={(e) => set(f.key, Number(e.target.value))} />
                  <p className="text-xs font-mono text-slate-600">{answers[f.key] ?? f.min}</p>
                </div>
              )}
              {f.type === "file" && (
                <div className="mt-1 space-y-1">
                  <input type="file" accept={f.accept || ""} onChange={(e) => onFile(f.key, e.target.files?.[0])} />
                  {answers[f.key]?.filename && <p className="text-xs text-slate-500">{answers[f.key].filename} · {(answers[f.key].size / 1024).toFixed(1)} KB</p>}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} disabled={busy} data-testid="filler-submit-btn">
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Form sharing dialog — Phase 3.40
// ============================================================
function ShareFormDialog({ form, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // Create-share form state
  const [mode, setMode] = useState("public");
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxSubmissions, setMaxSubmissions] = useState("");
  const [inviteeText, setInviteeText] = useState("");
  const [sendEmails, setSendEmails] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forms/${form.id}/shares`);
      setShares(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load shares");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [form.id]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const emails = mode === "invite"
        ? inviteeText
            .split(/[\s,;]+/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        : [];
      if (mode === "invite" && emails.length === 0) {
        toast.error("Add at least one invitee email");
        setCreating(false);
        return;
      }
      const payload = {
        mode,
        label: label || null,
        expires_at: expiresAt ? `${expiresAt}T23:59:59Z` : null,
        max_submissions: maxSubmissions ? parseInt(maxSubmissions, 10) : null,
        invitee_emails: emails,
        send_emails: mode === "invite" ? sendEmails : false,
      };
      const { data } = await api.post(`/forms/${form.id}/shares`, payload);
      if (mode === "public") {
        toast.success("Public link generated");
      } else if (sendEmails) {
        const invitesRes = data.invites || [];
        const okCount = invitesRes.filter((i) => (i.last_sent_status || "").startsWith("ok")).length;
        const failCount = invitesRes.length - okCount;
        if (okCount === invitesRes.length && invitesRes.length > 0) {
          toast.success(`${okCount} invite email${okCount === 1 ? "" : "s"} sent`);
        } else if (okCount > 0 && failCount > 0) {
          toast.warning(
            `Sent ${okCount} of ${invitesRes.length} invite email${invitesRes.length === 1 ? "" : "s"} — ${failCount} failed. Check the table below.`,
          );
        } else {
          // Surface the first error so users see WHY the send failed
          // (Resend not configured, sender not verified, etc.) instead of
          // a misleading "success" toast.
          const firstErr = invitesRes
            .map((i) => i.last_sent_status)
            .find((s) => s && !s.startsWith("ok"));
          const detail = firstErr ? ` — ${firstErr.replace(/^error:\s*/, "")}` : "";
          toast.error(`Could not send invite email${invitesRes.length === 1 ? "" : "s"}${detail}`);
        }
      } else {
        toast.success(`${emails.length} invite link${emails.length === 1 ? "" : "s"} generated`);
      }
      setLabel(""); setExpiresAt(""); setMaxSubmissions(""); setInviteeText(""); setSendEmails(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create share");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (sid, label) => {
    if (!window.confirm(`Revoke "${label || "share"}"? Anyone with the link will lose access immediately.`)) return;
    try {
      await api.delete(`/forms/${form.id}/shares/${sid}`);
      toast.success("Share revoked");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't revoke");
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const resendInvite = async (sid, invite) => {
    try {
      const { data } = await api.post(`/forms/${form.id}/shares/${sid}/resend/${invite.id}`);
      if (data.ok) toast.success(`Re-sent to ${invite.email}`);
      else toast.error(`Re-send failed: ${data.error}`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-send failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-emerald-600" /> Share {form.name}</DialogTitle>
          <DialogDescription>Generate a public link or send personalized invite emails. Revoke any time.</DialogDescription>
        </DialogHeader>

        <form onSubmit={create} className="space-y-4 mt-4 pb-4 border-b border-slate-100" data-testid="share-form-create">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMode("public")}
              className={`p-3 rounded-md border text-left transition ${mode === "public" ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200 hover:border-slate-400"}`}
              data-testid="share-mode-public"
            >
              <p className="font-medium text-sm">Public link</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Anyone with the URL submits anonymously.</p>
            </button>
            <button
              type="button"
              onClick={() => setMode("invite")}
              className={`p-3 rounded-md border text-left transition ${mode === "invite" ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200 hover:border-slate-400"}`}
              data-testid="share-mode-invite"
            >
              <p className="font-medium text-sm">Email invites</p>
              <p className="text-[11px] text-slate-500 mt-0.5">One-time link per invitee, tagged with their email.</p>
            </button>
          </div>

          <div>
            <Label className="text-xs">Label (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Q3 field survey" className="mt-1.5" data-testid="share-label" />
          </div>

          {mode === "invite" && (
            <>
              <div>
                <Label className="text-xs">Invitee emails (one per line, or comma/space separated)</Label>
                <Textarea
                  value={inviteeText}
                  onChange={(e) => setInviteeText(e.target.value)}
                  placeholder="field-worker-1@ngo.org&#10;field-worker-2@ngo.org"
                  rows={4}
                  className="mt-1.5 text-sm font-mono"
                  data-testid="share-invitees"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                  className="accent-emerald-500"
                  data-testid="share-send-emails"
                />
                Send invite emails immediately via Array Engine
                <span className="text-[11px] text-slate-400">(otherwise you can copy URLs from the table below)</span>
              </label>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Expires (optional)</Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-1.5" data-testid="share-expiry" />
            </div>
            <div>
              <Label className="text-xs">Max submissions (optional)</Label>
              <Input type="number" min={1} value={maxSubmissions} onChange={(e) => setMaxSubmissions(e.target.value)} placeholder="Unlimited" className="mt-1.5" data-testid="share-cap" />
            </div>
          </div>

          <Button
            type="submit"
            disabled={creating}
            className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium w-full"
            data-testid="share-create-btn"
          >
            {creating ? "Creating…" : mode === "public" ? "Generate public link" : "Generate invites"}
          </Button>
        </form>

        <div className="mt-5">
          <p className="label-overline mb-3">Active shares</p>
          {loading ? (
            <p className="text-sm text-slate-400 py-3">Loading…</p>
          ) : shares.length === 0 ? (
            <p className="text-sm text-slate-400 py-3" data-testid="share-empty">No shares yet — create one above.</p>
          ) : (
            <ul className="space-y-3" data-testid="share-list">
              {shares.map((s) => {
                const isRevoked = s.revoked;
                const expiredCheck = s.expires_at && new Date(s.expires_at) < new Date();
                const cappedCheck = s.max_submissions && s.submission_count >= s.max_submissions;
                const status = isRevoked ? "revoked" : expiredCheck ? "expired" : cappedCheck ? "capped" : "active";
                const url = s.mode === "public" ? `${window.location.origin}/forms/${s.token}` : null;
                return (
                  <li key={s.id} className="border border-slate-200 rounded-md p-3" data-testid={`share-item-${s.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                          {s.label || (s.mode === "public" ? "Public link" : `Invite batch · ${(s.invites || []).length} emails`)}
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                            status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                            status === "expired" ? "bg-amber-50 border-amber-200 text-amber-700" :
                            status === "capped" ? "bg-blue-50 border-blue-200 text-blue-700" :
                            "bg-rose-50 border-rose-200 text-rose-700"
                          }`}>{status}</span>
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {s.mode === "public" ? "Public" : "Invite"}
                          {" · "}{s.submission_count || 0} submission{s.submission_count === 1 ? "" : "s"}
                          {s.max_submissions ? ` / ${s.max_submissions}` : ""}
                          {s.expires_at ? ` · expires ${s.expires_at.slice(0, 10)}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {url && (
                          <Button size="sm" variant="outline" onClick={() => copy(url)} data-testid={`share-copy-${s.id}`}><span className="text-xs">Copy URL</span></Button>
                        )}
                        {!s.revoked && (
                          <Button size="sm" variant="outline" onClick={() => revoke(s.id, s.label)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`share-revoke-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {s.mode === "invite" && (s.invites || []).length > 0 && (
                      <ul className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid={`share-invites-${s.id}`}>
                        {s.invites.map((inv) => {
                          const inviteUrl = `${window.location.origin}/forms/invite/${inv.token}`;
                          return (
                            <li key={inv.id} className="flex items-center justify-between gap-2 text-xs">
                              <span className="font-mono text-slate-600 truncate">{inv.email}</span>
                              <span className="flex items-center gap-1.5 shrink-0">
                                {inv.used_at ? (
                                  <span className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">Used</span>
                                ) : (
                                  <>
                                    {inv.last_sent_status?.startsWith("ok") ? (
                                      <span className="text-slate-500">Sent</span>
                                    ) : inv.last_sent_status ? (
                                      <span className="text-amber-700">Send failed</span>
                                    ) : (
                                      <span className="text-slate-400">Not sent</span>
                                    )}
                                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => copy(inviteUrl)}>Copy</Button>
                                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => resendInvite(s.id, inv)} data-testid={`invite-resend-${inv.id}`}>
                                      {inv.last_sent_status ? "Re-send" : "Send"}
                                    </Button>
                                  </>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

